import { ReactNode } from 'react';

// Individual context providers
import { ClientProvider, useClient } from './client/ClientContext';
import { SupplierProvider, useSupplier } from './supplier/SupplierContext';
import { SaleProvider, useSale } from './sale/SaleContext';
import { BlogProvider, useBlog } from './blog/BlogContext';
import { TeamProvider, useTeam } from './team/TeamContext';

type ProviderProps = {
  children: ReactNode;
}

/**
 * RegisterProvider - Composite provider that wraps all domain-specific providers
 *
 * This maintains backward compatibility while allowing individual contexts
 * to be used separately for better performance (fewer re-renders).
 */
export function RegisterProvider({ children }: ProviderProps) {
  return (
    <ClientProvider>
      <SupplierProvider>
        <SaleProvider>
          <BlogProvider>
            <TeamProvider>
              {children}
            </TeamProvider>
          </BlogProvider>
        </SaleProvider>
      </SupplierProvider>
    </ClientProvider>
  );
}

/**
 * useRegister - Backward compatible hook that aggregates all domain contexts
 *
 * For new code, prefer using specific hooks:
 * - useClient() for client/clientGroup
 * - useSupplier() for supplier
 * - useSale() for sale/shippingCompany
 * - useBlog() for blogPost/author/banner
 * - useTeam() for buyer/seller
 */
export function useRegister() {
  const clientContext = useClient();
  const supplierContext = useSupplier();
  const saleContext = useSale();
  const blogContext = useBlog();
  const teamContext = useTeam();

  return {
    // Client domain
    client: clientContext.client,
    setClient: clientContext.setClient,
    updateClient: clientContext.updateClient,
    clientGroup: clientContext.clientGroup,
    setClientGroup: clientContext.setClientGroup,

    // Supplier domain
    supplier: supplierContext.supplier,
    setSupplier: supplierContext.setSupplier,
    updateSupplier: supplierContext.updateSupplier,

    // Sale domain
    sale: saleContext.sale,
    setSale: saleContext.setSale,
    updateSale: saleContext.updateSale,
    shippingCompany: saleContext.shippingCompany,
    setShippingCompany: saleContext.setShippingCompany,
    updateShippingCompany: saleContext.updateShippingCompany,

    // Blog domain
    blogPost: blogContext.blogPost,
    setBlogPost: blogContext.setBlogPost,
    updateBlogPost: blogContext.updateBlogPost,
    author: blogContext.author,
    setAuthor: blogContext.setAuthor,
    updateAuthor: blogContext.updateAuthor,
    banner: blogContext.banner,
    setBanner: blogContext.setBanner,

    // Team domain
    buyer: teamContext.buyer,
    setBuyer: teamContext.setBuyer,
    updateBuyer: teamContext.updateBuyer,
    seller: teamContext.seller,
    setSeller: teamContext.setSeller,
    updateSeller: teamContext.updateSeller,
  };
}

// Re-export individual hooks for gradual migration
export { useClient } from './client/ClientContext';
export { useSupplier } from './supplier/SupplierContext';
export { useSale } from './sale/SaleContext';
export { useBlog } from './blog/BlogContext';
export { useTeam } from './team/TeamContext';
